<template>
  <menu-layout :loading="loading">
    <label-crops-modal 
      v-if="showLabelModal" 
      :label_name="selectedLabelName" 
      :filters="current_filters"
      :total_count="selectedLabelCount"
      @close_modal="closeLabelModal"
    ></label-crops-modal>
    <row>
      <col3>
        <filter-tree
            ref="filterTree"
            :initial_filter="[
                {'type':27, 'value':''},
                {'type':17, 'value':'No'},
                {'type':11, 'value':'No'},
                {'type':61, 'value':'No'}
               ]"
            @load="load"
            @update="update"
            :max_results="max_results"></filter-tree>
        <filter-save-load
            :filters="filters"
            :limit="limit"
            page-name="myboxes"
            @load="loadSavedFilter"
        ></filter-save-load>
      </col3>
      <col9>
        <row>
          <col12>
            <!-- Grouped display -->
            <row v-if="is_grouped && images.length > 0">
              <col12>
                <row>
                  <col3 v-for="image in images" :key="image.label">
                    <grouped-label-item 
                      :item="image" 
                      :sensitive-blur="sensitiveBlurForGroupedItem(image)"
                      @open_modal="openLabelModal"
                    ></grouped-label-item>
                  </col3>
                </row>
              </col12>
            </row>
            <!-- Normal display -->
            <grid v-else
                  ref="grid"
                  :status_chips_mode="'myboxes'"
                  :show_relabel_dropdown="true"
                  @reload="load"
                  :items="images"
                  v-on:remove_item="removeGridItem"
                  @relabel="relabel_item"
                  @annotations-selected="update_selection"
                  @selection-change="update_selection">
              <template v-slot:menu>
                <col2>
                  <dropdown-menu v-if="images.length > 0"
                                 :menu_items="menu_items"
                  ></dropdown-menu>
                </col2>
                <col5>
                  <search-label-autocomplete
                      ref="relabelSearch"
                      v-if="images.length > 0"
                      style="float:left; max-width: 350px;"
                      :placeholder="$t('progress.search_field')"
                      v-model="class_name_relabel"
                      :clear_on_select="false"
                      @label-selected="onRelabelLabelSelect"
                      @cleared="relabel_cleared"
                  ></search-label-autocomplete>
                  <v-btn
                      :disabled="!canRelabel"
                      v-if="images.length > 0"
                      @click="relabel()"
                      id="relabel">
                    {{$t('myboxes.btn_change')}}
                  </v-btn>
                  <v-btn
                      :disabled="!canAddFullImageBbox"
                      v-if="images.length > 0"
                      @click="add_full_image_bbox"
                      id="add-full-image-bbox"
                      class="ml-2">
                    {{$t('myboxes.btn_add_full_image_bbox')}}
                  </v-btn>
                  <div class="d-flex align-center action-with-queue">
                    <v-btn
                        :disabled="!canRelabelInFilter"
                        v-if="images.length > 0"
                        @click="relabel_in_filter()"
                        id="relabel-in-filter">
                      {{$t('myboxes.btn_change_all_in_filter')}}
                    </v-btn>
                    <relabel-in-filter-jobs-button
                        v-if="images.length > 0 || relabelJobCount > 0"
                        ref="relabelJobsButton"
                    />
                  </div>
                </col5>
              </template>
            </grid>
          </col12>
        </row>
      </col9>
    </row>
  </menu-layout>
</template>

<script>
import Grid from "@/components/Grid";
import FilterMixin from "@/mixins/FilterMixin";
import FilterTree from "@/components/asset_filter/FilterTree";
import MenuLayout from "@/components/layout/MenuLayout";
import Row from "@/components/layout/Row";
import Col3 from "@/components/layout/Col3";
import Col9 from "@/components/layout/Col9";
import Col5 from "@/components/layout/Col5";
import Col2 from "@/components/layout/Col2";
import Col12 from "@/components/layout/Col12";
import DropdownMenu from "@/components/layout/DropdownMenu";
import SearchLabelAutocomplete from "@/components/SearchLabelAutocomplete.vue";
import GroupedLabelItem from "@/components/GroupedLabelItem";
import LabelCropsModal from "@/components/LabelCropsModal";
import FilterSaveLoad from "@/components/FilterSaveLoad";
import RelabelInFilterJobsButton from "@/components/myboxes/RelabelInFilterJobsButton.vue";
import MyAssetsJobsWebSocketMixin from "@/mixins/MyAssetsJobsWebSocketMixin.js";
import LoadSettingsMixin from "@/mixins/LoadSettingsMixin";
import CurrentUserMixin from "@/mixins/CurrentUserMixin.vue";
import {
  getBlurUnlessMetaFromSettings,
  shouldBlurAssetMeta,
} from '@/utils/sensitiveImageBlur';
import { filterQueryHasTypeValue, flatFilters } from "@/components/asset_filter/filterQuery";

export default {
  name: "MyBoxes",
  data: function () {
    return {
      images: [],
      class_name: '',
      translations: {},
      current_filter: false,
      class_name_relabel: null,
      toggle_all: true,
      max_results: false,
      loading: false,
      /** Bumps on each load so overlapping Loads cannot leave the overlay stuck. */
      boxLoadSeq: 0,
      filters: false,
      limit: false,
      bbox_ids_selected: [],
      is_grouped: false,
      showLabelModal: false,
      selectedLabelName: '',
      selectedLabelCount: 0,
      current_filters: [],
    }
  },
  computed: {
    menu_items() {
      return [
        {'title': this.$t('myboxes.dropdown_mark_selected'), 'on_click': this.mark_selected_bboxes_as_validated},
        {'title': this.$t('myboxes.dropdown_mark_all'), 'on_click': this.mark_all_bboxes_as_validated},
        {'title': this.$t('myboxes.dropdown_add_full_image_bbox'), 'on_click': this.add_full_image_bbox},
        {'title': this.$t('myboxes.dropdown_download_crops'), 'on_click': this.download_crops},
        {'title': this.$t('myboxes.dropdown_delete_selected'), 'on_click': this.delete_selected_annotations},
      ]
    },
    filters_includes_not_validated() {
      return filterQueryHasTypeValue(this.filters, 17, 'No');
    },
    filters_includes_validated() {
      return filterQueryHasTypeValue(this.filters, 17, 'Yes');
    },
    canRelabel() {
      const target = this.relabelTargetName;
      return this.bbox_ids_selected.length > 0 && Boolean(target);
    },
    canAddFullImageBbox() {
      return this.bbox_ids_selected.length > 0 && Boolean(this.relabelTargetName);
    },
    canRelabelInFilter() {
      return Boolean(this.relabelTargetName) && Boolean(this.filters);
    },
    relabelTargetName() {
      if (this.class_name_relabel) {
        return this.class_name_relabel;
      }
      const search = this.$refs.relabelSearch;
      const selected = search?.search_label_selected;
      if (selected && selected.name) {
        return selected.name;
      }
      const acVal = search?.$refs?.ac?.selected_value;
      if (acVal && typeof acVal === 'object' && acVal.name) {
        return acVal.name;
      }
      return null;
    },
    nlToScientific() {
      let mapping = {}
      let values = Object.values(this.translations);
      let keys = Object.keys(this.translations);
      for (let i = 0; i < values.length; i++) {
        mapping[values[i]] = keys[i]
      }
      return mapping;
    },
    items() {
      return this.images;
    },
    sensitiveBlurPolicy() {
      return getBlurUnlessMetaFromSettings(this.settings || []);
    },
    relabelJobCount() {
      return this.$store.getters['project_live_jobs/relabel_count'];
    },
  },
  components: {
    SearchLabelAutocomplete,
    RelabelInFilterJobsButton,
    DropdownMenu,
    Col12,
    Col2,
    Col5,
    Col9,
    Col3,
    Row,
    MenuLayout,
    FilterTree,
    Grid,
    GroupedLabelItem,
    LabelCropsModal,
    FilterSaveLoad
  },
  mixins: [FilterMixin, LoadSettingsMixin, CurrentUserMixin, MyAssetsJobsWebSocketMixin],
  methods: {
    sensitiveBlurForGroupedItem(item) {
      return shouldBlurAssetMeta(item && item.meta, this.sensitiveBlurPolicy, this.is_sensitive_image_blur_exempt);
    },
    openLabelModal(labelName, count) {
      this.selectedLabelName = labelName;
      this.selectedLabelCount = count;
      this.showLabelModal = true;
    },
    closeLabelModal() {
      this.showLabelModal = false;
      this.selectedLabelName = '';
      this.selectedLabelCount = 0;
    },
    async delete_selected_annotations() {
      if (this.bbox_ids_selected.length === 0) {
        return;
      }
      this.loading = true;
      try {
        await this.$api.box.delete_selected_annotations(this.bbox_ids_selected);
        await this.load();
      } catch (error) {
        console.error('Failed to delete selected annotations', error);
        this.loading = false;
      }
    },
    selectedAssetIds() {
      const ids = [];
      const seen = new Set();
      for (const image of this.images) {
        if (!this.bbox_ids_selected.includes(image.bbox_id)) {
          continue;
        }
        const assetId = image.asset_id || image.id;
        if (!assetId || seen.has(assetId)) {
          continue;
        }
        seen.add(assetId);
        ids.push(assetId);
      }
      return ids;
    },
    async add_full_image_bbox() {
      const label = this.relabelTargetName;
      const assetIds = this.selectedAssetIds();
      if (!label) {
        await this.$store.dispatch(
          'snackbars/add_failed_snack',
          this.$t('myboxes.full_image_bbox_select_label'),
        );
        return;
      }
      if (assetIds.length === 0) {
        await this.$store.dispatch(
          'snackbars/add_failed_snack',
          this.$t('myboxes.full_image_bbox_select_items'),
        );
        return;
      }
      this.loading = true;
      try {
        await this.$api.asset.addFullImageBboxesInBatches(assetIds, label);
        await this.$store.dispatch(
          'snackbars/add_success_snack',
          this.$t('myboxes.full_image_bbox_added', { count: assetIds.length, label }),
        );
        await this.load();
      } finally {
        this.loading = false;
      }
    },
    update(filters, limit) {
      this.filters = filters
      this.limit = limit
    },
    update_selection(bbox_ids) {
      this.bbox_ids_selected = bbox_ids;
    },
    onRelabelLabelSelect(label) {
      if (!label) {
        this.class_name_relabel = null;
      } else {
        this.class_name_relabel = label.name || label;
      }
    },
    relabel_cleared() {
      this.class_name_relabel = null;
    },
    removeItem(asset_id) {
      let filteredImages = this.images.filter((image) => {
        return image.id !== asset_id
      });
      this.images = filteredImages;
    },
    removeGridItem(asset_id) {
      this.removeItem(asset_id)
    },
    async mark_selected_bboxes_as_validated() {
      this.loading = true;
      try {
        await this.$api.box.mark_bboxes_validated(this.bbox_ids_selected);
        await this.load();
      } catch (error) {
        console.error('Failed to mark selected annotations validated', error);
        this.loading = false;
      }
    },
    async mark_all_bboxes_as_validated() {
      this.loading = true;
      try {
        await this.$api.box.mark_bboxes_validated(this.images.map((image) => image.bbox_id));
        await this.load();
      } catch (error) {
        console.error('Failed to mark all annotations validated', error);
        this.loading = false;
      }
    },
    async relabel() {
      const target = this.relabelTargetName;
      if (!target) {
        return;
      }
      this.$api.annotation.re_label(target, JSON.stringify(this.bbox_ids_selected)).then(() => {})
      //remove selected items from grid
      let filteredImages = this.images.filter((image) => {
        return !this.bbox_ids_selected.includes(image.bbox_id)
      });
      this.images = filteredImages;
    },
    async relabel_item(bbox_id, target) {
      if (!target || !bbox_id || bbox_id === '') {
        return;
      }
      const image = this.images.find((item) => item.bbox_id === bbox_id);
      if (!image || image.label === target) {
        return;
      }
      this.$refs.grid?.setRelabelingBboxId?.(bbox_id);
      try {
        await this.$api.annotation.re_label(target, JSON.stringify([bbox_id]));
        image.label = target;
      } catch (error) {
        console.error('Failed to relabel annotation', error);
      } finally {
        this.$refs.grid?.setRelabelingBboxId?.(null);
      }
    },
    async relabel_in_filter() {
      const target = this.relabelTargetName;
      if (!target || !this.filters) {
        return;
      }
      if (!window.confirm(this.$t('myboxes.confirm_change_all_in_filter', {label: target}))) {
        return;
      }
      await this.$api.annotation.re_label_in_filter(target, this.filters);
      this.$store.dispatch(
        'snackbars/add_success_snack',
        this.$t('myboxes.relabel_in_filter_job_started')
      );
      await this.$store.dispatch('project_live_jobs/fetch_active');
    },
    async download_crops() {
      if (flatFilters(this.filters || []).length === 0) {
        await this.$store.dispatch(
          'snackbars/add_failed_snack',
          this.$t('myboxes.download_crops_no_filter'),
        );
        return;
      }
      this.loading = true;
      try {
        const result = await this.$api.crop_download.create(this.filters);
        if (!result || result.error) {
          await this.$store.dispatch(
            'snackbars/add_failed_snack',
            (result && result.error) || this.$t('myboxes.download_crops_failed'),
          );
          return;
        }
        await this.$store.dispatch('snackbars/add_success_snack', {
          message: this.$t('myboxes.download_crops_started'),
          action_text: this.$t('myboxes.download_crops_go_to_downloads'),
          redirect: { name: 'Downloads' },
          timeout: 8000,
        });
        await this.$store.dispatch('project_live_jobs/fetch_active');
      } finally {
        this.loading = false;
      }
    },
    loadSavedFilter(filters, limit) {
      // Load saved filter into FilterTree and then load results
      if (this.$refs.filterTree) {
        this.$refs.filterTree.loadFilters(filters, limit);
      }
      // Update local state
      this.filters = filters;
      this.limit = limit;
      // Load the results
      this.load(filters, limit);
    },
    async load(filters, limit) {
      if (!filters) {
        filters = this.filters;
        limit = this.limit;
      }

      // Store current filters for modal
      this.current_filters = filters;

      const loadGeneration = ++this.boxLoadSeq;
      this.images = [];
      this.loading = true;

      try {
        const results = await this.$api.annotation.filter(filters, limit);
        if (loadGeneration !== this.boxLoadSeq) {
          return;
        }

        const items = Array.isArray(results && results.items) ? results.items : [];
        const max_results = results && results.max_results;
        const is_grouped = (results && results.is_grouped) || false;

        this.is_grouped = is_grouped;
        this.max_results = max_results;

        if (items.length === 0) {
          this.max_results = 0;
          return;
        }

        const built = [];
        for (let i = 0; i < items.length; i++) {
          if (loadGeneration !== this.boxLoadSeq) {
            return;
          }
          const item = items[i];
          try {
            const data = await this.$api.asset.get_crop(item.url, item.bbox, item.asset_id);
            const crops = (data && data.crops) || [];
            for (let j = 0; j < crops.length; j++) {
              const crop = crops[j];
              if (is_grouped) {
                built.push({
                  label: item.label,
                  count: item.count,
                  meta: item.meta,
                  data: crop.crop,
                  certainty: item.certainty,
                  bbox_id: item.bbox_id,
                  id: item.asset_id,
                  annotation_type: item.annotation_type,
                  is_grouped: true,
                  bbox_validated: item.bbox_validated === true,
                  asset_validated: item.asset_validated === true,
                  in_validation_set: item.in_validation_set === true,
                });
              } else {
                built.push({
                  meta: item.meta,
                  data: crop.crop,
                  certainty: item.certainty,
                  bbox_id: item.bbox_id,
                  label: item.label,
                  id: item.asset_id,
                  annotation_type: item.annotation_type,
                  bbox_validated: item.bbox_validated === true,
                  asset_validated: item.asset_validated === true,
                  in_validation_set: item.in_validation_set === true,
                });
              }
            }
          } catch (error) {
            console.error('Failed to load crop for annotation', item && item.bbox_id, error);
          }
        }

        if (loadGeneration !== this.boxLoadSeq) {
          return;
        }
        this.images = built;
      } catch (error) {
        console.error('Failed to load My Boxes grid', error);
        if (loadGeneration === this.boxLoadSeq) {
          this.images = [];
          this.max_results = 0;
        }
      } finally {
        if (loadGeneration === this.boxLoadSeq) {
          this.loading = false;
        }
      }
    }
  },
  watch: {
    toggle_all(value) {
      for (let i = 0; i < this.images.length; i++) {
        this.images[i].checked = value;
      }
    },
  },
}
</script>

<style scoped>
.action-with-queue {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
</style>